import { useState, useEffect } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';

interface OfflineIndicatorProps {
  onRetry?: () => void;
  className?: string;
}

/**
 * Component that displays a banner when the user is offline
 * Auto-hides when back online
 */
export function OfflineIndicator({ onRetry, className = '' }: OfflineIndicatorProps) {
  const isOnline = useOnlineStatus();
  const [show, setShow] = useState(!isOnline);
  const [wasOffline, setWasOffline] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShow(true);
      setWasOffline(true);
    } else if (wasOffline) {
      // Show "Back Online" message briefly
      setTimeout(() => {
        setShow(false);
        setWasOffline(false);
      }, 3000);
    }
  }, [isOnline, wasOffline]);

  const handleRetry = async () => {
    if (onRetry && isOnline) {
      setIsRetrying(true);
      try {
        await onRetry();
      } finally {
        setIsRetrying(false);
      }
    }
  };

  if (!show) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div
        className={`${
          isOnline
            ? 'bg-green-600'
            : 'bg-orange-600'
        } text-white shadow-lg`}
      >
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              {isOnline ? (
                <Wifi className="w-5 h-5 flex-shrink-0" />
              ) : (
                <WifiOff className="w-5 h-5 flex-shrink-0 animate-pulse" />
              )}

              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="font-semibold">
                  {isOnline ? 'Back Online' : 'You are offline'}
                </span>
                <span className="text-sm opacity-90">
                  {isOnline
                    ? 'Your connection has been restored'
                    : 'Some features may be limited. Changes will be saved locally.'}
                </span>
              </div>
            </div>

            {isOnline && onRetry && (
              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="flex items-center gap-2 px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                aria-label="Retry failed requests"
              >
                <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
                {isRetrying ? 'Retrying...' : 'Retry Failed Requests'}
              </button>
            )}

            {!isOnline && (
              <button
                onClick={() => setShow(false)}
                className="text-white/80 hover:text-white text-sm font-medium ml-auto"
                aria-label="Dismiss notification"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Minimal inline offline indicator (e.g., for footer or corner)
 */
export function OfflineIndicatorMinimal() {
  const isOnline = useOnlineStatus();

  if (isOnline) return null;

  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-100 text-orange-800 rounded-full text-sm"
      role="status"
      aria-label="Offline status"
    >
      <WifiOff className="w-4 h-4" />
      <span className="font-medium">Offline</span>
    </div>
  );
}
